
import {Vue, Component, PropSync} from 'vue-property-decorator';
import TeacherNotice from 'src/modules/teacher/TeacherNotice.vue';
import {openSubscribePage} from 'src/utils/NavigationUtils';
import {analyticsController} from 'src/modules/user/analytics/AnalyticsController';
import {getAssetItem} from 'src/utils/helpers';
import {type StripePrice, subscriptionService} from '../../user/subscription/SubscriptionService';
import {currentPromotion} from '../marketing/PromotionController';
import {userService} from '../../user/UserService';

@Component({
  components: {
    TeacherNotice,
  },
  methods: {
    getAssetItem,
  },
})
export default class FreemiumSubscriptionPopup extends Vue {
  private isDisplayed: boolean = false;
  private showBenefitsPopup: boolean = false;
  private promotion: number = 50;
  private isLoading: boolean = false;
  private slide: number = 1;
  private importMusicxmlPieceQuota: boolean = false;

  benefits = [
    {
      icon: 'mdi-infinity',
      key: 'unlimitedAccess',
    },
    {
      icon: 'mdi-robot',
      key: 'virtualTeacher',
    },
    {
      icon: 'mdi-chart-line',
      key: 'progressTracking',
    },
    {
      icon: 'mdi-file',
      key: 'importPieces',
    }
  ];

  testimonials = [
    { name: 1, id: '1' },
    { name: 2, id: '2' },
    { name: 3, id: '3' },
    { name: 4, id: '4' },
    { name: 5, id: '5' },
    { name: 6, id: '6' },
    { name: 7, id: '7' },
    { name: 8, id: '8' },
    { name: 9, id: '9' }
  ];

  mounted() {
    this.$root.$on('displayFreemiumPopup', (importMusicxmlPieceQuota = false) => {
      this.isDisplayed = true;
      this.importMusicxmlPieceQuota = importMusicxmlPieceQuota;
      analyticsController.logBasicAnalyticsEvent('freemium popup displayed', 'Freemium');
    });
  }

  get monthlyPrice() {
    return subscriptionService.monthlyPrice;
  }
  get monthlyPriceHumanReadable() {
    return subscriptionService.humanizeUnitAmount(this.monthlyPrice);
  }
  get monthlyReductedPriceHumanReadable() {
    const reducedPrice: StripePrice = {
      ...this.monthlyPrice,
      unit_amount: this.monthlyPrice?.unit_amount * (1 - this.promotion / 100),
    };
    return subscriptionService.humanizeUnitAmount(reducedPrice);
  }
  get mainTitle() {
    if (this.importMusicxmlPieceQuota) {
      return this.$t('freemiumSubscriptionPopup.quotaReached');
    }
    return this.$t('freemiumSubscriptionPopup.freeContentExhausted');
  }
  get mainSubtitle() {
    if (this.importMusicxmlPieceQuota) {
      return this.$t('freemiumSubscriptionPopup.importUnlimited');
    }
    return this.$t('freemiumSubscriptionPopup.unlockToday');
  }
  get isLandscape() {
    return this.$q.screen.width > this.$q.screen.height;
  }
  get showAsActionPopup() {
    return this.$q.screen.gt.sm || this.$q.screen.width < this.$q.screen.height;
  }
  get nextSundayDate(): string {
    const now = new Date();
    const nextSunday = new Date(now);

    // Calculate days until next Monday (0 = Sunday, 1 = Monday, etc.)
    const daysUntilMonday = (8 - now.getDay()) % 7;
    const daysToAdd = daysUntilMonday === 0 ? 7 : daysUntilMonday;

    nextSunday.setDate(now.getDate() + daysToAdd);
    nextSunday.setHours(0, 0, 0, 0);

    // Format in French with proper capitalization
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    };

    const formatted = nextSunday.toLocaleDateString('fr-FR', options);
    // Capitalize first letter
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  }
  get tomorrowDate() {
    const now = new Date();
    const tomorrow = new Date(now);

    tomorrow.setDate(now.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);

    // Format in French with proper capitalization
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    };

    const formatted = tomorrow.toLocaleDateString('fr-FR', options);
    // Capitalize first letter
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  }
  get isUserIsInBPaymentGroup() {
    return userService.user.paymentGroupTesting === 'B';
  }

  get translatedBenefits() {
    return this.benefits.map(benefit => ({
      ...benefit,
      text: this.$t(`freemiumSubscriptionPopup.benefits.${benefit.key}`)
    }));
  }

  get translatedTestimonials() {
    return this.testimonials.map(testimonial => ({
      ...testimonial,
      text: this.$t(`freemiumSubscriptionPopup.testimonials.${testimonial.id}.text`),
      author: this.$t(`freemiumSubscriptionPopup.testimonials.${testimonial.id}.author`)
    }));
  }

  showBenefits(): void {
    this.showBenefitsPopup = true;
    analyticsController.logBasicAnalyticsEvent('freemium popup benefits shown', 'Freemium');
  }
  closeBenefitsPopup(): void {
    this.showBenefitsPopup = false;
    analyticsController.logBasicAnalyticsEvent('freemium popup benefits closed', 'Freemium');
  }

  closePopup(): void {
    analyticsController.logBasicAnalyticsEvent('freemium popup closed', 'Freemium');
    this.isDisplayed = false;
  }

  goToSubscribe(): void {
    analyticsController.logBasicAnalyticsEvent('freemium popup subscribe clicked', 'Freemium');
    this.isDisplayed = false;
    openSubscribePage.bind(this)(this.$route.path, 'freemiumPopup', undefined, undefined, true);
  }

  subscribe(): void {
    this.isLoading = true;
    analyticsController.logBasicAnalyticsEvent('freemium popup subscribe for month clicked', 'Freemium');
    subscriptionService
      .redirectToCheckout(subscriptionService.monthlyPrice, currentPromotion, 'subscribe', true)
      .then((val) => {
        if (val === 'ios_purchase_success') {
          this.$router.push('/subscribe/success');
        }
      })
      .finally(() => {
        this.isLoading = false;
      });
  }
}
